/**
 * Structured 400 error thrown from services / repositories when input fails
 * domain validation. Caught by `apiHandler` (`@auctogon/observability`) which
 * converts it into a `{ success: false, error, fieldErrors }` envelope at
 * status 400 — keeping field-level messages inline-display-friendly without
 * each service needing to import NextResponse.
 *
 * Use sparingly: routes that already return `NextResponse.json({success:false,...},
 * {status:400})` directly are fine — this class exists for callers deep enough
 * in the stack that throwing is cleaner than threading a structured result
 * back up.
 *
 * SECURITY: the `message` and `fieldErrors` values are surfaced VERBATIM to
 * the client in the 400 envelope. Both MUST be static developer-authored
 * strings (or template literals with developer-controlled interpolations like
 * field names) — never derived from a caught error, raw DB output, or user
 * input. Doing `throw new ApiValidationError(caughtErr.message)` or
 * `throw new ApiValidationError(req.body.someValue)` would leak internals /
 * echo unsanitized input back to the client. See
 * `.claude/rules/error-handling-chain.md` (banned-patterns table).
 */
export class ApiValidationError extends Error {
  readonly status = 400 as const;
  readonly fieldErrors: Record<string, string>;

  /**
   * @param message - User-actionable, developer-authored static string. Surfaced
   *   verbatim in the 400 envelope's `error` field. Must NOT be derived from a
   *   caught error or user input — see class-level SECURITY note.
   * @param fieldErrors - Map of field name → user-actionable message. Defaults
   *   to `{}`. Same sanitization contract as `message`.
   */
  constructor(message: string, fieldErrors: Record<string, string> = {}) {
    super(message);
    this.name = 'ApiValidationError';
    this.fieldErrors = fieldErrors;
  }
}
